import {
    PayloadAction,
    createAsyncThunk,
    createEntityAdapter,
    createSlice,
} from '@reduxjs/toolkit';
import { RootState } from '../../../app/store';
import * as saleService from '../../../service/sale.service';
import {
    Sale,
    SaleRequest,
    SaleSearchCriteria,
    SaleSummary,
    getDefaultSaleSearchCriteria,
} from '../../../service/model/store.model';
import { showNotification } from '../../alerts/alert.slice';
import { NotificationType } from '../../../shared/enums';
import { getAxiosErrorMessage } from '../../../service/axios/axios.call.service';
import { PaginatedResponse } from '../../../service/model/paginated-response';
import { fetchProducts } from '../products/product.slice';

export interface SaleState {
    isLoading: boolean;
    sales: Sale[];
    summary?: SaleSummary;
    page?: PaginatedResponse<Sale>;
    totalPages: number;
    currentPage: number;
    totalRecords: number;
}

const saleAdapter = createEntityAdapter({
    selectId: (sale: Sale) => sale.id,
    sortComparer: (a, b) => b.saleDate?.localeCompare(a.saleDate),
});

export const initialState = saleAdapter.getInitialState({
    isLoading: false,
    sales: [],
    totalPages: 0,
    currentPage: 0,
    totalRecords: 0,
} as SaleState);

export const fetchSaleSummary = createAsyncThunk<SaleSummary>(
    'sales/summary',
    async (_, { rejectWithValue }) => {
        return saleService.getSaleSummary().catch((errors) => {
            return rejectWithValue(errors);
        });
    }
);

export const fetchSales = createAsyncThunk<
    PaginatedResponse<Sale>,
    SaleSearchCriteria
>('sales/page', async (pagination, { rejectWithValue }) => {
    return saleService.getSales(pagination).catch((errors) => {
        return rejectWithValue(errors);
    });
});

export const createSale = createAsyncThunk<Sale, SaleRequest>(
    'sales/create',
    async (request, { rejectWithValue, dispatch }) => {
        return saleService
            .createSale(request)
            .then((response) => {
                dispatch(
                    showNotification({
                        message: 'Sale created successfully',
                        type: NotificationType.Success,
                    })
                );
                dispatch(fetchSales(getDefaultSaleSearchCriteria()));
                dispatch(fetchSaleSummary());
                dispatch(fetchProducts());
                return response;
            })
            .catch((errors) => {
                dispatch(
                    showNotification({
                        message: getAxiosErrorMessage(errors.response),
                        type: NotificationType.Error,
                    })
                );
                return rejectWithValue(errors);
            });
    }
);

export const updateSale = createAsyncThunk<Sale, SaleRequest>(
    'sales/update',
    async (request, { rejectWithValue, dispatch }) => {
        return saleService
            .updateSale(request)
            .then((response) => {
                dispatch(
                    showNotification({
                        message: 'Sale updated successfully',
                        type: NotificationType.Success,
                    })
                );
                dispatch(fetchSales(getDefaultSaleSearchCriteria()));
                dispatch(fetchSaleSummary());
                dispatch(fetchProducts());
                return response;
            })
            .catch((errors) => {
                dispatch(
                    showNotification({
                        message: getAxiosErrorMessage(errors.response),
                        type: NotificationType.Error,
                    })
                );
                return rejectWithValue(errors);
            });
    }
);

export const deleteSale = createAsyncThunk<number, number>(
    'sales/delete',
    async (request, { rejectWithValue, dispatch }) => {
        return saleService
            .deleteSale(request)
            .then((response) => {
                dispatch(
                    showNotification({
                        message: 'Sale deleted successfully',
                        type: NotificationType.Success,
                    })
                );
                dispatch(fetchSales(getDefaultSaleSearchCriteria()));
                dispatch(fetchSaleSummary());
                dispatch(fetchProducts());
                return response;
            })
            .catch((errors) => {
                dispatch(
                    showNotification({
                        message: getAxiosErrorMessage(errors.response),
                        type: NotificationType.Error,
                    })
                );
                return rejectWithValue(errors);
            });
    }
);

const saleSlice = createSlice({
    name: 'sale',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchSaleSummary.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(
                fetchSaleSummary.fulfilled,
                (state, action: PayloadAction<SaleSummary>) => {
                    state.summary = action.payload;
                    state.isLoading = false;
                }
            )
            .addCase(fetchSaleSummary.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(fetchSales.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(
                fetchSales.fulfilled,
                (state, action: PayloadAction<PaginatedResponse<Sale>>) => {
                    saleAdapter.setAll(state, action.payload.data);
                    state.currentPage = action.payload.currentPage;
                    state.totalPages = action.payload.totalPages;
                    state.totalRecords = action.payload.totalRecords;
                    state.isLoading = false;
                }
            )
            .addCase(fetchSales.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(createSale.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(
                createSale.fulfilled,
                (state, action: PayloadAction<Sale>) => {
                    saleAdapter.addOne(state, action.payload);
                    state.isLoading = false;
                }
            )
            .addCase(createSale.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(updateSale.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(
                updateSale.fulfilled,
                (state, action: PayloadAction<Sale>) => {
                    saleAdapter.upsertOne(state, action.payload);
                    state.isLoading = false;
                }
            )
            .addCase(updateSale.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(deleteSale.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(
                deleteSale.fulfilled,
                (state, action: PayloadAction<number>) => {
                    saleAdapter.removeOne(state, action.payload);
                    state.isLoading = false;
                }
            )
            .addCase(deleteSale.rejected, (state) => {
                state.isLoading = false;
            });
    },
});

export const { selectAll: selectSales, selectById: selectSaleById } =
    saleAdapter.getSelectors<RootState>((state) => state.sales);

export const selectIsLoading = (state: RootState) => state.sales.isLoading;
export const selectSaleSummary = (state: RootState) => state.sales.summary;
export const selectCurrentPage = (state: RootState) => state.sales.currentPage;
export const selectTotalPages = (state: RootState) => state.sales.totalPages;
export const selectTotalRecords = (state: RootState) =>
    state.sales.totalRecords;

export default saleSlice.reducer;
